<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { VueFlow, MarkerType } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import ScenariosAPI from 'dashboard/api/scenarios';
import TemplatesAPI from 'dashboard/api/messageTemplates';
import Mres3API from 'dashboard/api/mres3';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ScenarioZoomControls from '../ScenarioZoomControls.vue';
import StepEdge from './StepEdge.vue';
import StepNode from './StepNode.vue';
import VersionControls from './VersionControls.vue';
import RunHistory from './RunHistory.vue';
import SimulationLab from './SimulationLab.vue';
import {
  arrangeGraph,
  connectionError,
  editorIssues,
  finishBranch,
  insertStep,
  isInlineEnd,
  makeEdge,
  pruneInlineEnds,
} from './editorGraph';
import { useEditorHistory } from './useEditorHistory';
import FieldValue from './FieldValue.vue';
import ConditionEditor from './ConditionEditor.vue';
import {
  conditionField,
  conditionMessages,
  conditionRuleValue,
} from './conditions';
import TemplatePicker from './TemplatePicker.vue';
import SourcePicker from './SourcePicker.vue';
import StartSettings from './StartSettings.vue';
import MessageSettings from './MessageSettings.vue';
import { messageValidationMessages } from '../templates/appointmentMessages';
import {
  defaultSchedule,
  scheduleSummary,
  startMessages,
} from './startSchedule';
import { eventLabel } from './eventLabel';
import {
  defaults,
  fromGraph,
  toGraph,
  previousFields,
  outputKeys,
} from './graph';
import { settingsMessages, waitDateFields, waitSources } from './nodeSettings';
import en from './en.json';
import ru from './ru.json';

const props = defineProps({ scenario: { type: Object, required: true } });
const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: {
      ...en,
      ...conditionMessages.en,
      ...settingsMessages.en,
      ...startMessages.en,
      ...messageValidationMessages.en,
    },
    ru: {
      ...ru,
      ...conditionMessages.ru,
      ...settingsMessages.ru,
      ...startMessages.ru,
      ...messageValidationMessages.ru,
    },
  },
});
const router = useRouter();
const initial = fromGraph(props.scenario.graph, props.scenario.trigger);
const nodes = ref(initial.nodes);
const edges = ref(initial.edges);
const name = ref(props.scenario.name);
const catalog = ref(null);
const templates = ref([]);
const selectedId = ref('trigger');
const selectedEdge = ref(null);
const inspectorOpen = ref(false);
const inspectorTab = ref('settings');
const lifecycle = ref(props.scenario);
const showRuns = ref(false);
const simulationOpened = ref(false);
const showSimulation = ref(false);
const simulationButton = ref(null);
const closeSimulation = async () => {
  showSimulation.value = false;
  await nextTick();
  simulationButton.value?.$el?.focus();
};
const runsButton = ref(null);
const closeRuns = async () => {
  showRuns.value = false;
  await nextTick();
  runsButton.value?.$el?.focus();
};
const versionsSupported = computed(() =>
  Number.isInteger(lifecycle.value.editor_revision)
);
const picker = ref(null);
const pickerDialog = ref(null);
const replacement = ref(null);
const replaceDialog = ref(null);
const nodeDialog = ref(null);
const nodeDraft = ref(null);
const showIssues = ref(false);
const loading = ref(true);
const saving = ref(false);
const checking = ref(false);
const notice = ref('');
const error = ref('');
const preview = ref(null);
const showPreview = ref(false);
const highlightPreview = ref(false);
const sourceId = ref('');
const eventAt = ref(new Date().toISOString());
const previous = ref({});
const appointments = ref([]);
const query = ref('');
let searchVersion = 0;
let flow;

const payload = computed(() => ({
  name: name.value,
  ...toGraph(nodes.value, edges.value),
}));
const serialized = () => JSON.stringify(payload.value);
const baseline = ref(serialized());
const dirty = computed(() => serialized() !== baseline.value);
const trigger = computed(
  () => nodes.value.find(node => node.id === 'trigger').data
);
const selected = computed(() =>
  nodes.value.find(node => node.id === selectedId.value)
);
const nodeIssues = computed(() => {
  if (!nodeDraft.value || !catalog.value) return [];
  return editorIssues(
    nodes.value.map(node =>
      node.id === selectedId.value ? { ...node, data: nodeDraft.value } : node
    ),
    edges.value,
    catalog.value,
    templates.value
  ).filter(
    item =>
      (item.node === selectedId.value ||
        (selectedId.value === 'trigger' &&
          [
            'previous_context_unavailable',
            'changed_requires_update',
            'appointment_context_unavailable',
            'patient_source_required',
          ].includes(item.code))) &&
      !['openOutput', 'unreachable_node'].includes(item.code)
  );
});
const fields = computed(() => catalog.value?.fields || []);
const dateFields = computed(() => waitDateFields(catalog.value));
const previousRequired = computed(() => previousFields(payload.value.graph));
const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const triggerEvent = computed(() =>
  trigger.value.mode === 'schedule' ? 'scheduled' : trigger.value.event
);
const conditionCatalog = computed(() =>
  trigger.value.entity === 'patients'
    ? { ...catalog.value, condition_contexts: ['patient'] }
    : catalog.value
);
const visibleNodes = computed(() =>
  nodes.value.filter(node => !isInlineEnd(node))
);
const traceNodes = computed(() =>
  highlightPreview.value
    ? new Set((preview.value?.trace || []).map(step => step.node))
    : new Set()
);
const previewEdges = computed(() => {
  if (!highlightPreview.value) return new Set();
  const trace = preview.value?.trace || [];
  const visited = new Set();
  if (trace.length) visited.add('trigger:next');
  trace.forEach((step, index) => {
    const next = trace[index + 1];
    if (!next) return;
    let output = 'next_unknown';
    if (step.type === 'condition')
      output = step.result ? 'next_true' : 'next_false';
    if (step.type === 'wait') output = 'next';
    const edge = edges.value.find(
      item =>
        item.source === step.node &&
        item.sourceHandle === output &&
        item.target === next.node
    );
    if (edge) visited.add(edge.id);
  });
  return visited;
});
const fieldFor = name_ =>
  fields.value.find(field => field.name === name_) || {};
const sourceLabel = source => t(source === 'event' ? 'eventSource' : source);
const nodeLabel = node =>
  node ? node.data.label || t(node.data.kind) : t('editor.endBranch');
const outputLabel = (node, output) =>
  output === 'next_success'
    ? t(`goal_${node.data.success_on || 'accepted'}`)
    : t(output);
const sourceSummary = data => {
  const channel = data.channel
    ? t(`channel_${data.channel}`)
    : t('editor.channelMissing');
  const source =
    data.source_mode === 'appointment'
      ? t('editor.appointmentSource')
      : catalog.value?.inboxes.find(item => item.id === data.inbox_id)?.name ||
        t('sourceMissing');
  return `${channel} · ${source}`;
};
const nodeIcons = {
  trigger: 'i-lucide-zap',
  condition: 'i-lucide-git-branch',
  wait: 'i-lucide-clock',
  message: 'i-lucide-send',
  availability: 'i-lucide-user-check',
  action: 'i-lucide-send',
  exit: 'i-lucide-flag',
};
const sourceLabels = computed(() =>
  Object.fromEntries(
    waitSources(triggerEvent.value).map(source => [source, sourceLabel(source)])
  )
);
const summary = node => {
  const { data } = node;
  if (data.kind === 'trigger')
    return data.mode === 'schedule'
      ? scheduleSummary(data, t)
      : eventLabel(data.event, t);
  if (data.kind === 'condition')
    return `${t(`conditionEditor.${data.definition.context || 'appointment'}`)}${data.definition.context === 'appointment_service' ? ` · ${t(data.definition.collection_match === 'none' ? 'conditionEditor.noProduct' : 'conditionEditor.anyProduct')}` : ''}: ${
      data.definition.rules
        .map(
          rule =>
            `${conditionField(catalog.value, rule).editor_visible === false ? t('conditionEditor.legacyField') : conditionField(catalog.value, rule).label || t('field')} ${t(rule.op)}${['present', 'blank', 'changed', 'today'].includes(rule.op) ? '' : ` ${conditionRuleValue(catalog.value, rule, t)}`}`
        )
        .join(' · ') || t('noRules')
    }`;
  if (data.kind === 'wait') {
    if (data.mode === 'window')
      return `${data.window_from || '—'} – ${data.window_to || '—'}`;
    const duration = t(`duration_${data.unit}`, { amount: data.amount ?? '—' });
    if (data.mode === 'fixed') return t('editor.fixedWait', { duration });
    return t(`editor.wait_${data.direction}`, {
      duration,
      field: fieldFor(data.field).label || t('field'),
    });
  }
  if (data.kind === 'availability') return sourceSummary(data);
  if (['action', 'message'].includes(data.kind))
    return (
      templates.value.find(item => item.id === data.template_id)?.name ||
      t('noTemplate')
    );
  return t('editor.endBranch');
};
const issues = computed(() => {
  const local = catalog.value
    ? editorIssues(nodes.value, edges.value, catalog.value, templates.value)
    : [];
  const combined = [...local, ...(preview.value?.errors || [])];
  return combined.filter(
    (item, index) =>
      combined.findIndex(
        other =>
          other.node === item.node &&
          other.code === item.code &&
          other.output === item.output
      ) === index
  );
});
const issueNodes = computed(
  () => new Set(issues.value.map(item => item.node)).size
);
const issueText = item => {
  if (Object.hasOwn(startMessages.en.scheduleErrors, item.code))
    return t(`scheduleErrors.${item.code}`);
  return item.code === 'openOutput'
    ? t('editor.openOutput', {
        output: outputLabel(
          nodes.value.find(node => node.id === item.node),
          item.output
        ),
      })
    : t(
        ['invalidConnection', 'selfConnection', 'cycleConnection'].includes(
          item.code
        )
          ? `editor.${item.code}`
          : item.code
      );
};
const errorsAt = id => issues.value.filter(item => item.node === id);
const displayedEdges = computed(() =>
  edges.value
    .filter(
      edge => !isInlineEnd(nodes.value.find(node => node.id === edge.target))
    )
    .map(edge => ({
      ...edge,
      type: 'editable',
      markerEnd: MarkerType.ArrowClosed,
      data: { highlighted: previewEdges.value.has(edge.id) },
    }))
);
const nodeOutputs = (id, data) =>
  outputKeys(data.kind).map(output => {
    const edge = edges.value.find(
      item => item.source === id && item.sourceHandle === output
    );
    return {
      id: output,
      label: outputLabel({ data }, output),
      connected: !!edge,
      terminal: isInlineEnd(nodes.value.find(node => node.id === edge?.target)),
    };
  });
const openNodeSettings = async () => {
  nodeDraft.value = JSON.parse(JSON.stringify(selected.value.data));
  if (selectedId.value === 'trigger') {
    nodeDraft.value.mode ||= 'event';
    nodeDraft.value.schedule = {
      ...defaultSchedule(defaultTimezone),
      ...nodeDraft.value.schedule,
    };
  }
  await nextTick();
  nodeDialog.value.open();
};
const closeNodeSettings = () => {
  nodeDraft.value = null;
  inspectorOpen.value = false;
};
const selectNode = (id, configure = true) => {
  selectedId.value = id;
  selectedEdge.value = null;
  inspectorOpen.value = true;
  inspectorTab.value = 'settings';
  showPreview.value = false;
  if (selected.value && configure) {
    inspectorOpen.value = false;
    openNodeSettings();
  }
};
const history = useEditorHistory(serialized, snapshot => {
  const restored = JSON.parse(snapshot);
  const graph = fromGraph(restored.graph, restored.trigger);
  name.value = restored.name;
  nodes.value = graph.nodes;
  edges.value = graph.edges;
  if (!nodes.value.some(node => node.id === selectedId.value))
    selectedId.value = 'trigger';
  selectedEdge.value = null;
});
const applyNodeSettings = () => {
  if (!nodeDraft.value || nodeIssues.value.length) return;
  history.edit(() => {
    selected.value.data = JSON.parse(JSON.stringify(nodeDraft.value));
  });
  nodeDialog.value.close();
};
const focusNode = (id, configure = true) => {
  const node = nodes.value.find(item => item.id === id);
  if (!node) return;
  selectNode(id, configure);
  nextTick(() =>
    flow?.setCenter(node.position.x + 128, node.position.y + 100, {
      zoom: 0.9,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : 200,
    })
  );
};
const focusIssue = item => {
  const connectionIssue = ['openOutput', 'missing_connection'].includes(
    item.code
  );
  focusNode(item.node, !connectionIssue);
  if (connectionIssue) inspectorTab.value = 'connections';
};
const arrange = () => {
  history.edit(() => {
    nodes.value = arrangeGraph(nodes.value, edges.value);
  });
  nextTick(() => flow?.fitView({ padding: 0.18, maxZoom: 1, duration: 0 }));
  notice.value = t('editor.arranged');
};
const failureMessage = failure => {
  if (failure.response?.status === 404) return t('notFound');
  if (failure.response?.status === 403) return t('noPermission');
  return t('error');
};

const showStart = () => {
  const start = nodes.value.find(node => node.id === 'trigger');
  flow?.setViewport({
    x: 40 - start.position.x * 0.85,
    y: 120 - start.position.y * 0.85,
    zoom: 0.85,
  });
};

async function loadAppointments() {
  searchVersion += 1;
  const version = searchVersion;
  appointments.value = [];
  if (!trigger.value.connection_id) return;
  try {
    const { data } = await Mres3API.records(
      trigger.value.connection_id,
      trigger.value.entity || 'appointments',
      { mres3_query: query.value }
    );
    if (version === searchVersion) appointments.value = data.records;
  } catch (failure) {
    if (version === searchVersion) error.value = failureMessage(failure);
  }
}

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const [meta, messages] = await Promise.all([
      ScenariosAPI.nodeCatalog(),
      TemplatesAPI.get(),
    ]);
    catalog.value = meta.data;
    templates.value = messages.data || [];
    await loadAppointments();
  } catch (failure) {
    error.value = failureMessage(failure);
  } finally {
    loading.value = false;
    await nextTick();
    showStart();
  }
};

watch(
  () => [trigger.value.connection_id, trigger.value.entity, trigger.value.mode],
  () => {
    sourceId.value = '';
    previous.value = {};
    preview.value = null;
    loadAppointments();
  }
);
watch(
  [payload, sourceId, eventAt, previous],
  () => {
    preview.value = null;
    notice.value = '';
  },
  { deep: true, flush: 'sync' }
);
onMounted(load);

const pickerSource = computed(() =>
  nodes.value.find(node => node.id === picker.value?.source)
);
const pickerEdge = computed(() =>
  edges.value.find(
    edge =>
      edge.source === picker.value?.source &&
      edge.sourceHandle === picker.value?.output
  )
);
const applyConnection = connection => {
  history.edit(() => {
    edges.value = edges.value.filter(
      edge =>
        !(
          edge.source === connection.source &&
          edge.sourceHandle === connection.sourceHandle
        )
    );
    edges.value.push(
      makeEdge(connection.source, connection.sourceHandle, connection.target)
    );
    nodes.value = pruneInlineEnds(nodes.value, edges.value);
  });
  notice.value = t('editor.connectionChanged');
};
const connect = connection => {
  const normalized = {
    ...connection,
    sourceHandle: connection.sourceHandle || 'next',
  };
  const reason = connectionError(nodes.value, edges.value, normalized);
  if (reason) {
    error.value = t(`editor.${reason}`);
    return;
  }
  const existing = edges.value.find(
    edge =>
      edge.source === normalized.source &&
      edge.sourceHandle === normalized.sourceHandle
  );
  if (existing?.target === normalized.target) return;
  if (existing) {
    replacement.value = { connection: normalized, oldTarget: existing.target };
    replaceDialog.value.open();
    return;
  }
  applyConnection(normalized);
};
const deleteNode = () => {
  if (selectedId.value === 'trigger') return;
  history.edit(() => {
    edges.value = edges.value.filter(
      edge =>
        edge.source !== selectedId.value && edge.target !== selectedId.value
    );
    nodes.value = nodes.value.filter(node => node.id !== selectedId.value);
    nodes.value = pruneInlineEnds(nodes.value, edges.value);
  });
  selectedId.value = 'trigger';
  notice.value = t('editor.stepDeleted');
};
const removeEdge = id => {
  history.edit(() => {
    edges.value = edges.value.filter(edge => edge.id !== id);
    nodes.value = pruneInlineEnds(nodes.value, edges.value);
  });
  selectedEdge.value = null;
  notice.value = t('editor.connectionRemoved');
};
const addRoute = () =>
  nodeDraft.value.routes.push({ channel: '', inbox_id: null });
const moveRoute = (index, delta) => {
  const { routes } = nodeDraft.value;
  const target = index + delta;
  if (target < 0 || target >= routes.length) return;
  [routes[index], routes[target]] = [routes[target], routes[index]];
};
const outputTarget = output => {
  const target = edges.value.find(
    edge => edge.source === selectedId.value && edge.sourceHandle === output
  )?.target;
  return isInlineEnd(nodes.value.find(node => node.id === target))
    ? '__end__'
    : target || '';
};
const applyFinish = (source, output) => {
  if (nodes.value.length >= 101 && !nodes.value.some(isInlineEnd)) {
    error.value = t('editor.nodeLimit');
    return;
  }
  history.edit(() => {
    const result = finishBranch(
      nodes.value,
      edges.value,
      source,
      output,
      `end${crypto.randomUUID()}`
    );
    nodes.value = result.nodes;
    edges.value = result.edges;
  });
  notice.value = t('editor.branchFinished');
};
const confirmReplacement = () => {
  if (replacement.value.finish)
    applyFinish(replacement.value.source, replacement.value.output);
  else applyConnection(replacement.value.connection);
  replaceDialog.value.close();
};
const setOutputFor = (source, output, target) => {
  if (target === '__end__') {
    const existing = edges.value.find(
      edge => edge.source === source && edge.sourceHandle === output
    );
    if (
      existing &&
      !isInlineEnd(nodes.value.find(node => node.id === existing.target))
    ) {
      replacement.value = {
        finish: true,
        source,
        output,
        oldTarget: existing.target,
      };
      replaceDialog.value.open();
    } else applyFinish(source, output);
    return;
  }
  if (target) connect({ source, sourceHandle: output, target });
  else removeEdge(`${source}:${output}`);
};
const setOutput = (output, target) =>
  setOutputFor(selectedId.value, output, target);
const addStep = (kind, continuation = null) => {
  if (kind === 'exit') {
    setOutputFor(picker.value.source, picker.value.output, '__end__');
    pickerDialog.value.close();
    return;
  }
  if (nodes.value.length >= 101) {
    error.value = t('editor.nodeLimit');
    pickerDialog.value.close();
    return;
  }
  if (pickerEdge.value && outputKeys(kind).length > 1 && !continuation) {
    picker.value.kind = kind;
    return;
  }
  const id = `n${crypto.randomUUID()}`;
  const result = insertStep({
    nodes: nodes.value,
    edges: edges.value,
    ...picker.value,
    kind,
    id,
    continuation: continuation || outputKeys(kind)[0],
    timezone: defaultTimezone,
  });
  if (trigger.value.entity === 'patients') {
    const data = result.nodes.find(node => node.id === id).data;
    if (kind === 'condition') {
      data.definition.context = 'patient';
      data.definition.rules.forEach(rule => {
        rule.source = 'patient';
        rule.field = '';
      });
    }
    if (kind === 'wait') data.mode = 'fixed';
    if (['message', 'availability'].includes(kind)) data.source_mode = 'exact';
  }
  history.edit(() => {
    nodes.value = result.nodes;
    edges.value = result.edges;
  });
  pickerDialog.value.close();
  notice.value = t('editor.stepAdded');
  focusNode(id);
};
const openPicker = (source, output = null, kind = null) => {
  if (nodes.value.length >= 101 && kind && kind !== 'exit') {
    error.value = t('editor.nodeLimit');
    return;
  }
  const node = nodes.value.find(item => item.id === source);
  const outputs = outputKeys(node.data.kind);
  if (!outputs.length) return;
  picker.value = {
    source,
    output: output || (outputs.length === 1 ? outputs[0] : null),
    kind: null,
    requestedKind: kind,
  };
  pickerDialog.value.open();
  if (picker.value.output && kind) addStep(kind);
};
const choosePickerOutput = output => {
  picker.value.output = output;
  if (picker.value.requestedKind) addStep(picker.value.requestedKind);
};
const insertOnEdge = id => {
  const edge = edges.value.find(item => item.id === id);
  if (edge) openPicker(edge.source, edge.sourceHandle);
};
const showVersionFailure = failure => {
  if (failure.response?.status === 409) {
    error.value = t('editor_conflict');
    return;
  }
  const errors = failure.response?.data?.errors;
  if (Array.isArray(errors) && errors.some(item => item.code)) {
    preview.value = { errors, trace: [], sends: 0 };
    showIssues.value = true;
    error.value = t('versions.actionFailed');
  } else error.value = failureMessage(failure);
};
const versionAction = async ({ action, version }) => {
  const statusOnly = ['pause', 'activate'].includes(action);
  if ((dirty.value && !statusOnly) || saving.value) return;
  saving.value = true;
  error.value = '';
  try {
    const { data } = await ScenariosAPI[action](props.scenario.id, {
      editor_revision: lifecycle.value.editor_revision,
      version,
    });
    lifecycle.value = data;
    if (!statusOnly) {
      const restored = fromGraph(data.graph, data.trigger);
      history.edit(() => {
        name.value = data.name;
        nodes.value = restored.nodes;
        edges.value = restored.edges;
      });
      baseline.value = serialized();
    }
    notice.value = t(`versions.done_${action}`);
    if (!selected.value) selectedId.value = 'trigger';
  } catch (failure) {
    showVersionFailure(failure);
  } finally {
    saving.value = false;
  }
};
const save = async () => {
  saving.value = true;
  error.value = '';
  const savedPayload = JSON.parse(serialized());
  try {
    const { data } = await ScenariosAPI.update(props.scenario.id, {
      ...savedPayload,
      editor_revision: lifecycle.value.editor_revision,
    });
    lifecycle.value = data;
    baseline.value = JSON.stringify(savedPayload);
    notice.value = t('versions.draftSaved');
  } catch (failure) {
    showVersionFailure(failure);
  } finally {
    saving.value = false;
  }
};
const check = async () => {
  checking.value = true;
  error.value = '';
  preview.value = null;
  try {
    const response = await ScenariosAPI.preview(props.scenario.id, {
      ...payload.value,
      source_id: sourceId.value,
      event_at: eventAt.value,
      previous: Object.fromEntries(
        previousRequired.value
          .filter(field => Object.hasOwn(previous.value, field))
          .map(field => [field, previous.value[field]])
      ),
    });
    preview.value = response.data;
    highlightPreview.value = true;
  } catch (failure) {
    if (failure.response?.data?.errors) preview.value = failure.response.data;
    else error.value = failureMessage(failure);
  } finally {
    checking.value = false;
  }
};
const beforeUnload = event => {
  if (dirty.value) {
    event.preventDefault();
    event.returnValue = '';
  }
};
const onHistoryKey = event => {
  if (
    showRuns.value ||
    showSimulation.value ||
    !(event.metaKey || event.ctrlKey) ||
    event.altKey ||
    document.querySelector('dialog[open]')
  )
    return;
  if (event.target.closest('input, textarea, select, [contenteditable="true"]'))
    return;
  const key = event.key.toLowerCase();
  if (key === 'z' || key === 'y') {
    event.preventDefault();
    if (key === 'y' || event.shiftKey) history.redo();
    else history.undo();
  }
};
window.addEventListener('keydown', onHistoryKey);
window.addEventListener('beforeunload', beforeUnload);
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeUnload);
  window.removeEventListener('keydown', onHistoryKey);
});
onBeforeRouteLeave(() => !dirty.value || window.confirm(t('unsaved')));
</script>

<template>
  <main
    class="flex flex-col w-full h-full min-h-0 min-w-0 overflow-hidden text-n-slate-12 bg-n-background"
  >
    <RunHistory
      v-if="showRuns"
      :scenario="lifecycle"
      :catalog="catalog"
      @close="closeRuns"
    />
    <SimulationLab
      v-if="simulationOpened && catalog"
      v-show="showSimulation"
      :visible="showSimulation"
      :scenario-id="scenario.id"
      :payload="payload"
      :catalog="catalog"
      :templates="templates"
      @close="closeSimulation"
    />
    <div
      v-show="!showRuns && !showSimulation"
      class="flex flex-col w-full h-full min-h-0 min-w-0"
    >
      <header
        class="flex shrink-0 flex-wrap items-center gap-3 px-5 py-3 max-sm:gap-2 max-sm:px-3 border-b border-n-weak bg-n-solid-1"
      >
        <Button
          :label="t('back')"
          :aria-label="t('back')"
          class="max-sm:[&>span.truncate]:hidden"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="router.push({ name: 'automation_scenarios_index' })"
        />
        <input
          v-model="name"
          :aria-label="t('title')"
          class="max-w-md flex-1 min-w-40 !mb-0 !border-0 !bg-transparent !shadow-none !px-0 font-semibold text-base"
        />
        <span class="text-xs text-n-slate-11" role="status">{{
          notice ||
          (dirty
            ? t('unsaved')
            : t(`editor.status_${lifecycle.status || 'draft'}`))
        }}</span>
        <div
          class="flex flex-wrap gap-2 ml-auto max-sm:w-full max-sm:justify-end"
        >
          <Button
            :aria-label="t('editor.undo')"
            :title="t('editor.undo')"
            icon="i-lucide-undo-2"
            variant="ghost"
            color="slate"
            :disabled="!history.canUndo.value || loading"
            @click="history.undo"
          />
          <Button
            :aria-label="t('editor.redo')"
            :title="t('editor.redo')"
            icon="i-lucide-redo-2"
            variant="ghost"
            color="slate"
            :disabled="!history.canRedo.value || loading"
            @click="history.redo"
          />
          <Button
            :label="t('editor.settings')"
            :aria-label="t('editor.settings')"
            class="max-sm:[&>span.truncate]:hidden"
            icon="i-lucide-settings-2"
            variant="ghost"
            color="slate"
            :disabled="loading"
            @click="selectNode('trigger')"
          />
          <Button
            :label="t('preview')"
            :aria-label="t('preview')"
            class="max-sm:[&>span.truncate]:hidden"
            icon="i-lucide-flask-conical"
            variant="ghost"
            :disabled="loading"
            @click="
              showPreview = !showPreview;
              inspectorOpen = true;
            "
          />
          <Button
            :label="saving ? t('saving') : t('save')"
            icon="i-lucide-save"
            :aria-label="t('save')"
            class="max-sm:[&>span.truncate]:hidden"
            :disabled="saving || loading || !dirty || !versionsSupported"
            :variant="dirty ? 'solid' : 'ghost'"
            @click="save"
          />
          <VersionControls
            :scenario="lifecycle"
            :dirty="dirty"
            :busy="saving || loading"
            @action="versionAction"
          />
          <Button
            ref="simulationButton"
            :label="t('simulation.title')"
            :aria-label="t('simulation.title')"
            icon="i-lucide-test-tubes"
            variant="ghost"
            :disabled="loading || saving"
            @click="
              simulationOpened = true;
              showSimulation = true;
            "
          />
          <Button
            ref="runsButton"
            :label="t('runs.title')"
            :aria-label="t('runs.title')"
            icon="i-lucide-list-tree"
            variant="ghost"
            color="slate"
            :disabled="loading || saving"
            @click="showRuns = true"
          />
        </div>
      </header>
      <p
        v-if="!loading && !versionsSupported"
        role="alert"
        class="px-5 py-2 m-0 text-xs text-n-ruby-11"
      >
        {{ t('versions.backendRequired') }}
      </p>
      <p
        v-else-if="lifecycle.has_draft && lifecycle.published_version"
        class="px-5 py-2 m-0 text-xs text-n-slate-11 border-b border-n-weak bg-n-solid-1"
      >
        {{ t('versions.draftNotice', { number: lifecycle.published_version }) }}
      </p>
      <p
        v-if="catalog?.runtime_mode === 'test'"
        class="shrink-0 px-5 py-2 m-0 text-xs border-b border-n-weak text-n-slate-11 bg-n-solid-1"
      >
        <span
          class="i-lucide-info inline-block size-3 me-1 align-middle"
          aria-hidden="true"
        />{{ t('draftOnly') }}
      </p>
      <p
        v-if="trigger.imported_from"
        class="px-5 py-2 m-0 text-xs border-b border-n-weak text-n-amber-11"
        role="status"
      >
        {{ t('importedDraft') }}
      </p>
      <p v-if="error" role="alert" class="px-5 py-3 m-0 text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div
        v-if="!loading && catalog"
        class="shrink-0 px-5 py-2 border-b border-n-weak bg-n-solid-1"
      >
        <div
          class="flex flex-wrap items-center gap-1.5 max-sm:flex-nowrap max-sm:overflow-x-auto"
        >
          <Button
            v-for="kind in catalog.node_types"
            :key="kind"
            class="shrink-0"
            :label="t(kind)"
            :icon="nodeIcons[kind]"
            :title="t(`editor.hints.${kind}`)"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="!selected || !outputKeys(selected.data.kind).length"
            @click="openPicker(selectedId, null, kind)"
          />
          <Button
            :aria-label="t('editor.arrange')"
            :title="t('editor.arrange')"
            icon="i-lucide-network"
            variant="ghost"
            color="slate"
            size="sm"
            class="ms-auto shrink-0"
            @click="arrange"
          />
          <Button
            :label="
              issues.length
                ? t('editor.issueCount', { count: issueNodes })
                : t('editor.structureReady')
            "
            :icon="issues.length ? 'i-lucide-triangle-alert' : 'i-lucide-check'"
            variant="ghost"
            :color="issues.length ? 'amber' : 'slate'"
            size="sm"
            :aria-expanded="showIssues"
            class="shrink-0"
            @click="showIssues = !showIssues"
          />
        </div>
        <p class="m-0 mt-1 text-xs text-n-slate-10 max-sm:hidden">
          {{ t('editor.connectHint') }}
        </p>
        <div v-if="showIssues" class="max-h-40 overflow-auto pt-2 space-y-1">
          <p class="text-xs text-n-slate-11">
            {{ t('editor.validationHint') }}
          </p>
          <button
            v-for="(item, index) in issues"
            :key="`${item.node}:${item.code}:${item.output || index}`"
            type="button"
            class="block w-full px-2 py-1 text-start text-xs text-n-amber-11 rounded hover:bg-n-alpha-1 focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="focusIssue(item)"
          >
            {{
              nodes.find(node => node.id === item.node)?.data.label ||
              (nodes.find(node => node.id === item.node)
                ? t(nodes.find(node => node.id === item.node).data.kind)
                : t('title'))
            }}: {{ issueText(item) }}
          </button>
        </div>
      </div>
      <p v-if="loading" class="p-6 text-sm" role="status">{{ t('loading') }}</p>
      <div
        v-else-if="catalog"
        class="relative flex flex-1 min-h-0 overflow-hidden"
      >
        <section
          class="relative flex-1 min-w-0 min-h-0"
          :aria-label="t('title')"
        >
          <VueFlow
            v-model:nodes="nodes"
            :edges="displayedEdges"
            :min-zoom="0.15"
            :max-zoom="1.5"
            :delete-key-code="null"
            :is-valid-connection="
              connection => !connectionError(nodes, edges, connection)
            "
            @connect="connect"
            @node-click="focusNode($event.node.id)"
            @pane-click="
              inspectorOpen = false;
              selectedEdge = null;
            "
            @edge-click="selectedEdge = $event.edge.id"
            @node-drag-start="history.begin"
            @node-drag-stop="history.end"
            @selection-drag-start="history.begin"
            @selection-drag-stop="history.end"
            @pane-ready="
              flow = $event;
              showStart();
            "
          >
            <Background :gap="24" />
            <ScenarioZoomControls />
            <template #edge-editable="edgeProps">
              <StepEdge v-bind="edgeProps" @insert="insertOnEdge" />
            </template>
            <template #node-mres3="{ data, id }">
              <StepNode
                :kind="data.kind"
                :icon="nodeIcons[data.kind]"
                :label="data.label"
                :summary="summary({ data })"
                :source="data.kind === 'message' ? sourceSummary(data) : ''"
                :outputs="nodeOutputs(id, data)"
                :issue="errorsAt(id).length ? issueText(errorsAt(id)[0]) : ''"
                :active="(inspectorOpen || !!nodeDraft) && selectedId === id"
                :visited="traceNodes.has(id)"
                :dimmed="
                  highlightPreview && traceNodes.size > 0 && !traceNodes.has(id)
                "
                @configure="focusNode(id)"
                @add="openPicker(id, $event)"
              />
            </template>
          </VueFlow>
          <nav
            v-if="selectedEdge"
            class="absolute flex flex-wrap justify-center gap-2 p-2 border rounded-xl shadow-sm bottom-5 left-1/2 -translate-x-1/2 bg-n-solid-1 border-n-weak"
            :aria-label="t('editor.connectionActions')"
          >
            <Button
              :label="t('editor.insertStep')"
              icon="i-lucide-plus"
              variant="ghost"
              size="sm"
              @click="insertOnEdge(selectedEdge)"
            />
            <Button
              v-if="selectedEdge"
              :label="t('removeConnection')"
              variant="ghost"
              size="sm"
              color="ruby"
              @click="removeEdge(selectedEdge)"
            />
          </nav>
        </section>
        <aside
          v-if="inspectorOpen || showPreview"
          class="w-[26rem] shrink-0 max-lg:absolute max-lg:inset-y-0 max-lg:right-0 max-lg:z-10 max-sm:w-full overflow-y-auto border-l border-n-weak bg-n-solid-1 p-5"
          :aria-label="selected ? nodeLabel(selected) : t('selectNode')"
        >
          <template v-if="selected && !showPreview">
            <div
              class="flex items-center justify-between pb-4 mb-4 border-b border-n-weak"
            >
              <h2 class="m-0 text-base font-semibold flex-1">
                {{ nodeLabel(selected) }}
              </h2>
              <Button
                :aria-label="t('editor.closePanel')"
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="sm"
                @click="inspectorOpen = false"
              />
              <Button
                v-if="selectedId !== 'trigger'"
                :aria-label="t('delete')"
                :title="t('delete')"
                icon="i-lucide-trash-2"
                class="shrink-0"
                variant="ghost"
                size="sm"
                color="ruby"
                @click="deleteNode"
              />
            </div>
            <div
              class="flex gap-1 mb-5 border-b border-n-weak"
              role="tablist"
              :aria-label="t('editor.nodePanel')"
            >
              <button
                v-for="tab in ['settings', 'connections']"
                :id="`node-tab-${tab}`"
                :key="tab"
                role="tab"
                :aria-selected="inspectorTab === tab"
                :aria-controls="`node-panel-${tab}`"
                type="button"
                class="px-3 py-2 text-sm border-b-2 -mb-px focus-visible:ring-2 focus-visible:ring-n-brand"
                :class="
                  inspectorTab === tab
                    ? 'border-n-brand text-n-brand font-medium'
                    : 'border-transparent text-n-slate-11'
                "
                @click="inspectorTab = tab"
              >
                {{ t(`editor.${tab}`) }}
              </button>
            </div>
            <div
              v-show="inspectorTab === 'settings'"
              id="node-panel-settings"
              role="tabpanel"
              aria-labelledby="node-tab-settings"
            >
              <p class="text-sm text-n-slate-11">{{ summary(selected) }}</p>
              <Button
                :label="t('nodeSettings.configure')"
                icon="i-lucide-sliders-horizontal"
                class="w-full"
                @click="openNodeSettings"
              />
            </div>
            <div
              v-show="inspectorTab === 'connections'"
              id="node-panel-connections"
              role="tabpanel"
              aria-labelledby="node-tab-connections"
            >
              <p class="text-xs leading-5 text-n-slate-11">
                {{ t('editor.connectionsHint') }}
              </p>
              <fieldset
                v-if="outputKeys(selected.data.kind).length"
                class="pt-4 mt-4 border-t border-n-weak space-y-3"
              >
                <legend class="text-xs font-semibold">
                  {{ t('outcomes') }}
                </legend>
                <label
                  v-for="output in outputKeys(selected.data.kind)"
                  :key="output"
                  class="block text-xs"
                  >{{ outputLabel(selected, output) }}
                  <select
                    :value="outputTarget(output)"
                    class="w-full mt-1 text-sm"
                    @change="
                      setOutput(output, $event.target.value);
                      $event.target.value = outputTarget(output);
                    "
                  >
                    <option value="">{{ t('editor.notConnected') }}</option>
                    <option value="__end__">{{ t('editor.endBranch') }}</option>
                    <option
                      v-for="target in visibleNodes.filter(
                        node => node.id !== selectedId && node.id !== 'trigger'
                      )"
                      :key="target.id"
                      :value="target.id"
                      :disabled="
                        !!connectionError(nodes, edges, {
                          source: selectedId,
                          sourceHandle: output,
                          target: target.id,
                        })
                      "
                    >
                      {{ nodeLabel(target) }} · {{ summary(target) }} ·
                      {{ nodes.indexOf(target) }}
                    </option>
                  </select>
                </label>
              </fieldset>
              <p v-else class="text-sm text-n-slate-11">
                {{ t('editor.noOutputs') }}
              </p>
            </div>
            <p
              v-for="item in errorsAt(selectedId)"
              :key="`${item.code}:${item.output || ''}`"
              role="alert"
              class="mt-3 text-xs text-n-ruby-11"
            >
              {{ issueText(item) }}
            </p>
          </template>
          <section
            v-if="showPreview"
            class="space-y-4"
            :aria-label="t('preview')"
          >
            <div class="flex items-center gap-2 border-b border-n-weak pb-3">
              <span
                class="i-lucide-flask-conical size-5 text-n-brand"
                aria-hidden="true"
              />
              <h2 class="text-base font-semibold m-0 flex-1">
                {{ t('preview') }}
              </h2>
              <Button
                :aria-label="t('editor.closePanel')"
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                @click="
                  showPreview = false;
                  highlightPreview = false;
                "
              />
            </div>
            <div class="flex flex-col items-start gap-2">
              <div>
                <p class="max-w-4xl text-xs leading-5 text-n-slate-11">
                  {{ t('previewHint') }}
                </p>
              </div>
              <Button
                :label="t('check')"
                class="shrink-0"
                :disabled="checking || loading || !sourceId"
                @click="check"
              />
            </div>
            <div class="flex flex-col gap-4">
              <label class="text-xs"
                >{{
                  trigger.entity === 'patients'
                    ? t('startSchedule.patients')
                    : t('sourceId')
                }}
                <div class="flex gap-2 mt-1">
                  <input
                    v-model="sourceId"
                    type="number"
                    min="1"
                    class="w-32"
                  /><select
                    v-model="sourceId"
                    :aria-label="
                      trigger.entity === 'patients'
                        ? t('startSchedule.patients')
                        : t('sourceId')
                    "
                    class="flex-1 text-xs"
                  >
                    <option value="">{{ t('choose') }}</option>
                    <option
                      v-for="row in appointments"
                      :key="row.source_id"
                      :value="String(row.source_id)"
                    >
                      #{{ row.source_id }} ·
                      {{
                        trigger.entity === 'patients'
                          ? [
                              row.data.lastname,
                              row.data.name,
                              row.data.secondname,
                            ]
                              .filter(Boolean)
                              .join(' ')
                          : `${row.data.planned_start_at || '—'} · ${row.data.doctor_lastname || ''}`
                      }}
                    </option>
                  </select>
                </div></label
              >
              <label class="text-xs"
                >{{ t('eventAt')
                }}<input
                  v-model="eventAt"
                  type="text"
                  class="w-full mt-1"
                  :placeholder="t('isoFormat')"
              /></label>
            </div>
            <p v-if="previousRequired.length" class="text-xs text-n-slate-11">
              {{ t('previousHint') }}
            </p>
            <div class="flex flex-wrap gap-3">
              <label
                v-for="field in previousRequired"
                :key="field"
                class="min-w-56 text-xs"
                >{{ fieldFor(field).label || field
                }}<FieldValue
                  v-model="previous[field]"
                  :field="fieldFor(field)"
                  :label="t('previous')"
              /></label>
            </div>
            <div v-if="preview" class="mt-4" role="status">
              <p class="text-xs font-semibold">
                {{ t('messagesSent', { count: preview.sends || 0 }) }}
              </p>
              <p
                v-for="item in preview.errors"
                :key="`${item.node}:${item.code}`"
                class="m-0 mb-2 text-xs text-n-ruby-11"
              >
                <button
                  class="underline"
                  @click="
                    focusNode(item.node);
                    showPreview = false;
                  "
                >
                  {{
                    nodes.find(node => node.id === item.node)?.data.kind
                      ? t(nodes.find(node => node.id === item.node).data.kind)
                      : t(item.node)
                  }}</button
                >: {{ issueText(item) }}
              </p>
              <ol class="m-0 pl-5 text-xs space-y-2" :aria-label="t('trace')">
                <li
                  v-for="(step, index) in preview.trace"
                  :key="`${step.node}:${index}`"
                  class="pb-3"
                >
                  <button
                    type="button"
                    class="flex items-center gap-2 text-start text-sm font-medium text-n-brand mb-1"
                    @click="
                      flow?.setCenter(
                        (nodes.find(node => node.id === step.node)?.position
                          .x || 0) + 128,
                        (nodes.find(node => node.id === step.node)?.position
                          .y || 0) + 100,
                        { zoom: 0.85 }
                      )
                    "
                  >
                    <span
                      :class="nodeIcons[step.type]"
                      class="size-4"
                      aria-hidden="true"
                    />
                    {{ nodeLabel(nodes.find(node => node.id === step.node)) }}
                  </button>
                  {{ t(step.type) }} ·
                  {{
                    typeof step.result === 'boolean'
                      ? t(step.result ? 'yes' : 'no')
                      : t(step.result)
                  }}
                  · {{ step.until || step.at }}
                  <p v-if="step.reason" class="m-0 mt-1 text-n-slate-11">
                    {{ t(step.reason) }}
                  </p>
                </li>
              </ol>
            </div>
          </section>
        </aside>
      </div>

      <Dialog
        ref="nodeDialog"
        width="3xl"
        :title="nodeDraft ? t(nodeDraft.kind) : t('nodeSettings.configure')"
        :description="t('nodeSettings.draftHint')"
        :aria-label="
          nodeDraft ? t(nodeDraft.kind) : t('nodeSettings.configure')
        "
        @close="closeNodeSettings"
        @confirm="applyNodeSettings"
      >
        <div
          v-if="nodeDraft && catalog"
          class="max-h-[60vh] overflow-y-auto overscroll-contain pr-2 space-y-5"
        >
          <Input v-model="nodeDraft.label" :label="t('nodeTitle')" />
          <template v-if="selectedId === 'trigger'">
            <label class="block mb-4 text-xs"
              >{{ t('source') }}
              <select
                v-model="nodeDraft.connection_id"
                class="w-full mt-1 text-sm"
              >
                <option :value="null">{{ t('choose') }}</option>
                <option
                  v-for="item in catalog.connections"
                  :key="item.id"
                  :value="item.id"
                  :disabled="!item.active"
                >
                  {{ item.companies_uuid }}
                </option>
              </select>
            </label>
            <StartSettings v-model="nodeDraft" :catalog="catalog" />
          </template>
          <ConditionEditor
            v-else-if="nodeDraft.kind === 'condition'"
            v-model="nodeDraft.definition"
            :catalog="conditionCatalog"
            :event="triggerEvent"
            :connection-id="trigger.connection_id"
          />
          <template v-else-if="nodeDraft.kind === 'wait'">
            <label class="block mb-4 text-xs"
              >{{ t('mode')
              }}<select v-model="nodeDraft.mode" class="w-full mt-1 text-sm">
                <option
                  v-for="mode in ['fixed', 'dynamic', 'window']"
                  :key="mode"
                  :value="mode"
                  :disabled="
                    trigger.entity === 'patients' && mode === 'dynamic'
                  "
                >
                  {{ t(mode) }}
                </option>
              </select></label
            >
            <label class="block mb-4 text-xs"
              >{{ t('timezone')
              }}<select
                v-model="nodeDraft.timezone"
                class="w-full mt-1 text-sm"
              >
                <option
                  v-for="zone in catalog.timezones"
                  :key="zone"
                  :value="zone"
                >
                  {{ zone }}
                </option>
              </select></label
            >
            <div v-if="nodeDraft.mode === 'window'" class="flex gap-3">
              <label class="flex-1 text-xs"
                >{{ t('from')
                }}<input
                  v-model="nodeDraft.window_from"
                  type="time"
                  class="w-full mt-1"
              /></label>
              <label class="flex-1 text-xs"
                >{{ t('to')
                }}<input
                  v-model="nodeDraft.window_to"
                  type="time"
                  class="w-full mt-1"
              /></label>
            </div>
            <template v-else>
              <template v-if="nodeDraft.mode === 'dynamic'">
                <p class="text-xs leading-5 text-n-slate-11">
                  {{ t('nodeSettings.fieldHint') }}
                </p>
                <label class="block mb-4 text-xs"
                  >{{ t('source')
                  }}<select
                    v-model="nodeDraft.source"
                    class="w-full mt-1 text-sm"
                  >
                    <option
                      v-if="!Object.hasOwn(sourceLabels, nodeDraft.source)"
                      :value="nodeDraft.source"
                      disabled
                    >
                      {{ t('nodeSettings.unavailable') }}
                    </option>
                    <option
                      v-for="(label, source) in sourceLabels"
                      :key="source"
                      :value="source"
                    >
                      {{ label }}
                    </option>
                  </select></label
                >
                <label class="block mb-4 text-xs"
                  >{{ t('field')
                  }}<select
                    v-model="nodeDraft.field"
                    class="w-full mt-1 text-sm"
                  >
                    <option value="">{{ t('choose') }}</option>
                    <option
                      v-if="
                        nodeDraft.field &&
                        !dateFields.some(
                          field => field.name === nodeDraft.field
                        )
                      "
                      :value="nodeDraft.field"
                      disabled
                    >
                      {{ t('nodeSettings.unavailable') }}
                    </option>
                    <option
                      v-for="field in dateFields"
                      :key="field.name"
                      :value="field.name"
                    >
                      {{ field.label || field.name }}
                    </option>
                  </select></label
                >
                <label class="block mb-4 text-xs"
                  >{{ t('dateFormat') }}
                  <select
                    v-model="nodeDraft.date_format"
                    class="w-full mt-1 text-sm"
                  >
                    <option value="">{{ t('choose') }}</option>
                    <option
                      v-for="format in catalog.date_formats"
                      :key="format"
                      :value="format"
                    >
                      {{ t(format) }}
                    </option>
                  </select>
                </label>
                <select
                  v-model="nodeDraft.direction"
                  :aria-label="t('before')"
                  class="w-full text-sm"
                >
                  <option value="before">{{ t('before') }}</option>
                  <option value="after">{{ t('after') }}</option>
                </select>
              </template>
              <label class="block mb-4 text-xs"
                >{{ t('amount')
                }}<input
                  v-model.number="nodeDraft.amount"
                  type="number"
                  min="0"
                  class="w-full mt-1"
              /></label>
              <select
                v-model="nodeDraft.unit"
                :aria-label="t('amount')"
                class="w-full text-sm"
              >
                <option v-for="unit in catalog.units" :key="unit" :value="unit">
                  {{ t(unit) }}
                </option>
              </select>
              <label
                v-if="nodeDraft.mode === 'dynamic'"
                class="block mt-4 text-xs"
                >{{ t('ifPast')
                }}<select
                  v-model="nodeDraft.if_past"
                  class="w-full mt-1 text-sm"
                >
                  <option value="skip">{{ t('skip') }}</option>
                  <option value="continue">{{ t('continue') }}</option>
                </select></label
              >
            </template>
          </template>
          <template
            v-else-if="['message', 'availability'].includes(nodeDraft.kind)"
          >
            <SourcePicker
              v-model:channel="nodeDraft.channel"
              v-model:inbox-id="nodeDraft.inbox_id"
              v-model:source-mode="nodeDraft.source_mode"
              :appointment-available="trigger.entity !== 'patients'"
              :catalog="catalog"
            />

            <div class="mt-5 pt-4 border-t border-n-weak">
              <TemplatePicker
                v-if="nodeDraft.kind === 'message'"
                :key="selectedId"
                v-model="nodeDraft.template_id"
                :channel="nodeDraft.channel"
                :templates="templates"
                @refresh="templates = $event"
              />
            </div>
            <p class="my-4 text-xs leading-5 text-n-slate-11">
              {{ t('mandatoryPreflight') }}
            </p>
            <template v-if="nodeDraft.kind === 'message'">
              <MessageSettings v-model:node="nodeDraft" :trigger="trigger" />
              <p
                v-if="trigger.entity === 'patients'"
                class="text-xs leading-5 text-n-slate-11"
              >
                {{ t('startSchedule.patientTemplate') }}
              </p>
              <label class="block mt-4 text-xs"
                >{{ t('successOn') }}
                <select
                  v-model="nodeDraft.success_on"
                  class="w-full mt-1 text-sm"
                >
                  <option
                    v-for="result in [
                      'accepted',
                      'delivered',
                      'read',
                      'response',
                    ]"
                    :key="result"
                    :value="result"
                    :disabled="
                      !catalog.supported_runtime_results?.includes(result)
                    "
                  >
                    {{ t(`goal_${result}`)
                    }}{{
                      catalog.supported_runtime_results?.includes(result)
                        ? ''
                        : ' — ' + t('editor.draftCapability')
                    }}
                  </option>
                </select>
              </label>
              <p class="text-xs leading-5 text-n-slate-11">
                {{ t('editor.supportedResults') }}
              </p>
              <p
                v-if="
                  !catalog.supported_runtime_results?.includes(
                    nodeDraft.success_on
                  )
                "
                class="text-xs text-n-amber-11"
              >
                {{ t('editor.resultUnsupported') }}
              </p>
              <label class="block mt-4 text-xs"
                >{{ t('resultTimeout')
                }}<input
                  v-model.number="nodeDraft.timeout_minutes"
                  type="number"
                  min="1"
                  max="525600"
                  class="w-full mt-1"
              /></label>
              <label class="block mt-4 text-xs"
                >{{ t('ttl')
                }}<input
                  v-model.number="nodeDraft.ttl_hours"
                  type="number"
                  min="0"
                  class="w-full mt-1"
              /></label>
            </template>
            <p class="my-4 text-xs leading-5 text-n-slate-11">
              {{ t('cascadeHint') }}
            </p>
          </template>
          <template v-else-if="nodeDraft.kind === 'action'">
            <p class="text-xs leading-5 text-n-slate-11">
              {{ t('legacyActionHint') }}
            </p>
            <TemplatePicker
              :key="selectedId"
              v-model="nodeDraft.template_id"
              :templates="templates"
              @refresh="templates = $event"
            />
            <p class="text-xs font-semibold">{{ t('routes') }}</p>
            <div
              v-for="(route, index) in nodeDraft.routes"
              :key="index"
              class="py-3 border-b border-n-weak"
            >
              <div class="flex items-center gap-1 mb-2">
                <span class="text-xs mr-auto">{{ index + 1 }}</span
                ><Button
                  icon="i-lucide-arrow-up"
                  :aria-label="t('up')"
                  variant="ghost"
                  size="sm"
                  :disabled="index === 0"
                  @click="moveRoute(index, -1)"
                /><Button
                  icon="i-lucide-arrow-down"
                  :aria-label="t('down')"
                  variant="ghost"
                  size="sm"
                  :disabled="index === nodeDraft.routes.length - 1"
                  @click="moveRoute(index, 1)"
                /><Button
                  :label="t('remove')"
                  variant="ghost"
                  size="sm"
                  @click="nodeDraft.routes.splice(index, 1)"
                />
              </div>
              <select
                v-model="route.channel"
                :aria-label="t('routes')"
                class="w-full text-sm"
                @change="route.inbox_id = null"
              >
                <option value="">{{ t('choose') }}</option>
                <option
                  v-for="channel in catalog.channels"
                  :key="channel"
                  :value="channel"
                >
                  {{ t(`channel_${channel}`) }}
                </option>
              </select>
              <select
                v-model="route.inbox_id"
                :aria-label="t('inbox')"
                class="w-full text-sm"
              >
                <option :value="null">{{ t('choose') }}</option>
                <option
                  v-for="inbox in catalog.inboxes.filter(
                    item => item.channel === route.channel
                  )"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>
            <Button
              :label="t('addRoute')"
              variant="ghost"
              class="my-3"
              :disabled="nodeDraft.routes.length >= 100"
              @click="addRoute"
            />
            <p class="text-xs leading-5 text-n-slate-11">
              {{ t('routesHint') }}
            </p>
            <label class="block mt-4 text-xs"
              >{{ t('ttl')
              }}<input
                v-model.number="nodeDraft.ttl_hours"
                type="number"
                min="0"
                class="w-full mt-1"
            /></label>
          </template>
          <details class="mt-5 pt-4 border-t border-n-weak text-xs">
            <summary class="cursor-pointer text-n-slate-11">
              {{ t('editor.notesAndDetails') }}
            </summary>
            <div class="mt-4">
              <label class="block mb-4 text-xs">
                {{ t('nodeNotes') }}
                <textarea
                  v-model="nodeDraft.notes"
                  rows="5"
                  class="w-full mt-1 text-sm"
                />
                <span class="text-n-slate-11">{{ t('notesHint') }}</span>
              </label>
              <details
                v-if="nodeDraft.source_settings || nodeDraft.imported_from"
                class="mb-4 text-xs"
              >
                <summary class="cursor-pointer">
                  {{ t('sourceSettings') }}
                </summary>
                <pre
                  class="mt-2 p-3 rounded-lg bg-n-slate-2 whitespace-pre-wrap break-words"
                  >{{
                    JSON.stringify(
                      nodeDraft.source_settings || nodeDraft.imported_from,
                      null,
                      2
                    )
                  }}</pre
                >
              </details>
            </div>
          </details>
          <ul
            v-if="nodeIssues.length"
            class="m-0 list-none text-sm text-n-amber-11 space-y-1"
            aria-live="polite"
          >
            <li v-for="item in nodeIssues" :key="`${item.node}:${item.code}`">
              <span v-if="item.node !== selectedId"
                >{{ nodeLabel(nodes.find(node => node.id === item.node)) }}:
              </span>
              {{ issueText(item) }}
            </li>
          </ul>
        </div>
        <template #footer>
          <div
            class="flex items-center justify-end gap-3 border-t border-n-weak pt-4"
          >
            <div class="flex items-center gap-3">
              <Button
                :label="t('cancel')"
                type="button"
                variant="faded"
                color="slate"
                @click="nodeDialog.close()"
              />
              <Button
                :label="t('conditionEditor.apply')"
                type="submit"
                :disabled="nodeIssues.length > 0"
              />
            </div>
          </div>
        </template>
      </Dialog>
      <Dialog
        ref="pickerDialog"
        :title="t('editor.addStep')"
        :cancel-button-label="t('cancel')"
        :show-confirm-button="false"
        @close="picker = null"
      >
        <template v-if="picker && pickerSource">
          <p class="m-0 text-sm text-n-slate-11">
            {{ t('editor.afterStep', { step: nodeLabel(pickerSource) }) }}
            <span v-if="picker.output">
              · {{ outputLabel(pickerSource, picker.output) }}</span
            >
          </p>
          <div v-if="!picker.output" class="space-y-2">
            <p class="text-sm font-medium">{{ t('editor.chooseOutput') }}</p>
            <Button
              v-for="output in outputKeys(pickerSource.data.kind)"
              :key="output"
              :label="outputLabel(pickerSource, output)"
              variant="outline"
              class="w-full"
              @click="choosePickerOutput(output)"
            />
          </div>
          <div v-else-if="picker.kind" class="space-y-2">
            <p class="text-sm">
              {{
                t('editor.chooseContinuation', {
                  step: nodeLabel(
                    nodes.find(node => node.id === pickerEdge.target)
                  ),
                })
              }}
            </p>
            <Button
              v-for="output in outputKeys(picker.kind)"
              :key="output"
              :label="
                outputLabel(
                  { data: defaults(picker.kind, defaultTimezone) },
                  output
                )
              "
              variant="outline"
              class="w-full"
              @click="addStep(picker.kind, output)"
            />
            <Button
              :label="t('editor.backToTypes')"
              variant="ghost"
              @click="picker.kind = null"
            />
          </div>
          <div v-else class="space-y-1">
            <p v-if="pickerEdge" class="text-xs text-n-slate-11">
              {{ t('editor.preserveContinuation') }}
            </p>
            <button
              v-for="kind in catalog.node_types"
              :key="kind"
              type="button"
              class="flex items-start gap-3 w-full p-3 text-start rounded-lg hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="false"
              @click="addStep(kind)"
            >
              <span
                :class="nodeIcons[kind]"
                class="size-5 mt-1 shrink-0 text-n-brand"
                aria-hidden="true"
              />
              <span>
                <span class="block text-sm font-semibold text-n-slate-12">{{
                  t(kind)
                }}</span>
                <span class="block mt-1 text-xs leading-5 text-n-slate-11">{{
                  t(`editor.hints.${kind}`)
                }}</span>
              </span>
            </button>
            <p v-if="pickerEdge" class="text-xs text-n-slate-11">
              {{ t('editor.exitInsertionHint') }}
            </p>
          </div>
        </template>
      </Dialog>
      <Dialog
        ref="replaceDialog"
        type="alert"
        :title="t('editor.replaceTitle')"
        :cancel-button-label="t('cancel')"
        :confirm-button-label="t('editor.replaceConfirm')"
        @close="replacement = null"
        @confirm="confirmReplacement"
      >
        <p v-if="replacement" class="m-0 text-sm text-n-slate-11">
          {{
            t('editor.replaceHint', {
              from: nodeLabel(
                nodes.find(node => node.id === replacement.oldTarget)
              ),
              to: replacement.finish
                ? t('editor.endBranch')
                : nodeLabel(
                    nodes.find(
                      node => node.id === replacement.connection.target
                    )
                  ),
            })
          }}
        </p>
      </Dialog>
    </div>
  </main>
</template>
